import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyIcon from '@mui/icons-material/Key';
import LogoutIcon from '@mui/icons-material/Logout';
import PasswordIcon from '@mui/icons-material/Password';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import type { MeResponse } from '@taqon/contracts';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { AccountSetupPage } from '../features/auth/AccountSetupPage';
import { BusinessCardPage } from '../features/hr/BusinessCardPage';
import {
  ChangePasswordDialog,
  RecoveryCodesDialog,
  useOwnCredential,
} from '../features/auth/AccountSecurity';
import taqonLogo from '../assets/taqon-logo.png';
import { SignInPage } from '../features/auth/SignInPage';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { GlobalSearchBox } from '../features/search/GlobalSearch';
import { hasCapability, useSession } from '../shared/session/session';
import { ErrorState, LoadingState } from '../shared/ui/page';
import { MODULES, ModuleProvider } from '../shared/ui/modules';
import { EASE, rise, sweep } from '../shared/ui/motion';
import {
  ACCENT_SHADOW,
  BRAND,
  COLOUR_CYCLE,
  MINT_GLOW,
  MINT_GLOW_STRONG,
  RAINBOW,
  accentGradient,
} from '../shared/ui/tokens';
import { PersonAvatar } from '../shared/ui/person';
import { NAVIGATION, filterNavigation, locate, visibleNavigation } from './navigation';

/**
 * Ends the session through the real logout route, which clears the server-side session. The CSRF
 * token goes in the header the API requires; a full reload then drops every cached query.
 */
async function signOut(csrfToken: string): Promise<void> {
  await fetch('/auth/logout', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': csrfToken },
  });
  window.location.assign('/');
}

const DRAWER_WIDTH = 256;
const RAIL_WIDTH = 72;
const COLLAPSE_KEY = 'ibms.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, String(value));
  } catch {
    // A preference only: the sidebar works without it.
  }
}

/**
 * Application frame (WCAG 2.1 AA target, FRD-CORE-041): skip link, header with the page context
 * and the account menu, a sidebar that is the primary navigation, and the main landmark.
 * On desktop the sidebar can collapse to icons; below `md` it is a drawer opened from the header.
 */
export function AppShell() {
  const session = useSession();
  const location = useLocation();

  // The setup link works signed out, and is the same page for anyone holding it (SDD §11.1.2).
  if (location.pathname === '/account-setup') return <AccountSetupPage />;
  // So does an employee's business card, for anyone who scans its code (CLR-65).
  const card = /^\/card\/([^/]+)\/?$/.exec(location.pathname);
  if (card?.[1] !== undefined) return <BusinessCardPage token={card[1]} />;
  if (session.isPending) {
    return (
      <Container sx={{ py: 6 }}>
        <LoadingState label="Loading your session" />
      </Container>
    );
  }
  if (session.isError) {
    return (
      <Container sx={{ py: 6 }}>
        <ErrorState message={session.error.message} />
      </Container>
    );
  }
  if (session.data === null) return <SignInPage />;
  return <SignedIn me={session.data} />;
}

/**
 * The module of the page in view, from the navigation model: the banner names it. A page outside
 * the navigation is in the workspace.
 */
function SignedIn({ me }: { me: MeResponse }) {
  const { pathname } = useLocation();
  const here = locate(pathname, visibleNavigation(me)) ?? locate(pathname, NAVIGATION);
  const tone = MODULES[here?.module ?? 'home'];
  return (
    <ModuleProvider value={tone}>
      <Frame me={me} />
    </ModuleProvider>
  );
}

/** Each block of a page rises into place, one after the other. */
const PAGE_ENTRANCE = {
  '& > .MuiStack-root > *': { animation: `${rise} 520ms ${EASE} backwards` },
  ...Object.fromEntries(
    [2, 3, 4, 5, 6].map((n) => [
      `& > .MuiStack-root > *:nth-of-type(${String(n)})`,
      { animationDelay: `${String((n - 1) * 70)}ms` },
    ]),
  ),
  '& > .MuiStack-root > *:nth-of-type(n + 7)': { animationDelay: '420ms' },
};

function Frame({ me }: { me: MeResponse }) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const rail = desktop && collapsed;
  const width = rail ? RAIL_WIDTH : DRAWER_WIDTH;

  const sidebar = (
    <SidebarContent
      me={me}
      rail={rail}
      onNavigate={() => {
        setMobileOpen(false);
      }}
      onExpand={() => {
        writeCollapsed(false);
        setCollapsed(false);
      }}
    />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <Link
        href="#main"
        sx={{
          position: 'absolute',
          left: -9999,
          '&:focus': { left: 8, top: 8, zIndex: 2000, bgcolor: 'background.paper', p: 1 },
        }}
      >
        Skip to main content
      </Link>

      <AppBar
        position="fixed"
        component="header"
        color="inherit"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${String(width)}px)` },
          ml: { md: `${String(width)}px` },
          bgcolor: 'rgba(255, 255, 255, 0.82)',
          backdropFilter: 'saturate(180%) blur(14px)',
          border: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          transition: `width 260ms ${EASE}, margin 260ms ${EASE}`,
          // A thin rainbow under the header; the mint activity line runs over it while busy.
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -1,
            height: 3,
            backgroundImage: RAINBOW,
          },
        }}
      >
        <Toolbar sx={{ gap: 1.5 }}>
          {!desktop && (
            <IconButton
              edge="start"
              aria-label="Open navigation"
              onClick={() => {
                setMobileOpen(true);
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          {!desktop && <BrandMark height={26} />}
          <BackButton />
          <PageContext />
          <Box sx={{ flexGrow: 1 }} />
          {hasCapability(me, 'platform.search:view') && <GlobalSearchBox compact={!desktop} />}
          {hasCapability(me, 'platform.notification:view') && <NotificationBell me={me} />}
          <AccountMenu me={me} />
        </Toolbar>
        <ActivityBar />
      </AppBar>

      <Box sx={{ width: { md: width }, flexShrink: { md: 0 }, transition: `width 260ms ${EASE}` }}>
        {desktop ? (
          <Drawer
            variant="permanent"
            open
            slotProps={{
              paper: {
                sx: {
                  width,
                  boxSizing: 'border-box',
                  border: 0,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  overflowX: 'hidden',
                  transition: `width 260ms ${EASE}`,
                },
              },
            }}
          >
            {sidebar}
            <Divider />
            <Box sx={{ p: 1, display: 'flex', justifyContent: rail ? 'center' : 'flex-end' }}>
              <Tooltip title={rail ? 'Expand navigation' : 'Collapse navigation'} placement="right">
                <IconButton
                  size="small"
                  aria-label={rail ? 'Expand navigation' : 'Collapse navigation'}
                  onClick={() => {
                    setCollapsed((value) => {
                      writeCollapsed(!value);
                      return !value;
                    });
                  }}
                >
                  {rail ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => {
              setMobileOpen(false);
            }}
            slotProps={{ paper: { sx: { width: DRAWER_WIDTH, border: 0 } } }}
          >
            {sidebar}
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        id="main"
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          outline: 'none',
          // Washes of mint and soft colours behind the page, light enough that secondary text keeps
          // its contrast on them.
          backgroundImage: [
            `radial-gradient(1100px 480px at 100% 0%, ${MINT_GLOW}, transparent 70%)`,
            'radial-gradient(800px 480px at 0% 0%, rgba(59, 130, 246, 0.08), transparent 70%)',
            'radial-gradient(900px 560px at 15% 100%, rgba(245, 158, 11, 0.1), transparent 70%)',
            'radial-gradient(800px 520px at 100% 100%, rgba(236, 72, 153, 0.08), transparent 70%)',
          ].join(', '),
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Toolbar />
        <Box sx={{ py: 3, px: { xs: 2, sm: 3, lg: 4 }, ...PAGE_ENTRANCE }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

/**
 * A thin mint line under the header while the page is loading or saving, so a click is seen to
 * have done something. It is decoration: each list and form announces its own state.
 */
function ActivityBar() {
  const busy = useIsFetching() + useIsMutating() > 0;
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: -1,
        height: 2,
        overflow: 'hidden',
        opacity: busy ? 1 : 0,
        transition: `opacity 240ms ${EASE}`,
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          width: '40%',
          backgroundImage: `linear-gradient(90deg, transparent, ${BRAND.mint}, ${BRAND.mintDark}, transparent)`,
          animation: `${sweep} 1.1s ease-in-out infinite`,
          animationPlayState: busy ? 'running' : 'paused',
        },
      }}
    />
  );
}

/**
 * Where the letters sit in `taqon-logo.png` (480 × 297 px, measured): the baseline of "ta" is row
 * 270, 91 % of the height, and the "a" is 140 rows tall; above them rises the Q's power mark.
 */
const LOGO_BASELINE = 270 / 297;
const LOGO_X_HEIGHT = 140 / 297;
/** Montserrat's capitals stand at 70 % of the font size. */
const CAP_HEIGHT = 0.7;

/**
 * The TAQON logo with "INFINITY" beside it, on one line: "INFINITY" sits on the baseline of the logo's
 * "ta", and its capitals are as tall as the logo's "a". An image's baseline is its bottom edge, so
 * the logo is moved down by the strip of image below its letters. On the collapsed rail the logo
 * stands alone and names the product itself.
 */
function BrandMark({ height, logoOnly = false }: { height: number; logoOnly?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', flexShrink: 0 }}>
      <Box
        component="img"
        src={taqonLogo}
        alt={logoOnly ? 'TAQON INFINITY' : ''}
        sx={{
          display: 'block',
          height,
          width: 'auto',
          position: 'relative',
          top: `${(height * (1 - LOGO_BASELINE)).toFixed(2)}px`,
        }}
      />
      {!logoOnly && (
        <Typography
          component="span"
          noWrap
          sx={{
            ml: `${(height * 0.3).toFixed(1)}px`,
            fontWeight: 800,
            letterSpacing: '0.06em',
            lineHeight: 1,
            fontSize: `${((height * LOGO_X_HEIGHT) / CAP_HEIGHT).toFixed(1)}px`,
          }}
        >
          INFINITY
        </Typography>
      )}
    </Box>
  );
}

function SidebarContent({
  me,
  rail,
  onNavigate,
  onExpand,
}: {
  me: MeResponse;
  rail: boolean;
  onNavigate: () => void;
  /** Leaves the icon rail, where there is no room to type; omitted when there is no rail. */
  onExpand?: () => void;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [focusSearch, setFocusSearch] = useState(false);
  // The rail shows icons only, so it always shows the whole menu.
  const groups = filterNavigation(visibleNavigation(me), rail ? '' : search);
  const leave = () => {
    setSearch('');
    onNavigate();
  };
  return (
    <Box
      component="nav"
      aria-label="Main"
      sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}
    >
      <Toolbar sx={{ px: rail ? 0 : 2.5, justifyContent: rail ? 'center' : 'flex-start' }}>
        <BrandMark height={rail ? 28 : 34} logoOnly={rail} />
      </Toolbar>
      <Divider />
      {rail ? (
        <Box sx={{ py: 0.75, display: 'flex', justifyContent: 'center' }}>
          <Tooltip title="Search menu" placement="right">
            <IconButton
              size="small"
              aria-label="Search menu"
              onClick={() => {
                setFocusSearch(true);
                onExpand?.();
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            px: 1.5,
            pt: 1.5,
            pb: 1,
            bgcolor: 'background.paper',
          }}
        >
          <TextField
            type="search"
            size="small"
            fullWidth
            autoFocus={focusSearch}
            placeholder="Search menu"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            onKeyDown={(event) => {
              const first = groups[0]?.items[0];
              if (event.key === 'Enter' && search.trim() !== '' && first !== undefined) {
                event.preventDefault();
                void navigate(first.to);
                leave();
              }
              if (event.key === 'Escape' && search !== '') {
                event.stopPropagation();
                setSearch('');
              }
            }}
            slotProps={{
              htmlInput: { 'aria-label': 'Search menu' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      )}
      {groups.length === 0 && (
        <Typography role="status" variant="body2" color="text.secondary" sx={{ px: 2.5, py: 1.5 }}>
          No menu entry matches “{search.trim()}”.
        </Typography>
      )}
      {groups.map((group, index) => {
        // Every entry takes the next colour of the cycle, across groups, for its icon tile.
        const offset = groups.slice(0, index).reduce((sum, g) => sum + g.items.length, 0);
        return (
          <List
            key={group.label ?? `group-${String(index)}`}
            dense
            sx={{ px: 1, py: 0.5 }}
            subheader={
              group.label !== null && !rail ? (
                <ListSubheader
                  disableSticky
                  sx={{
                    lineHeight: '32px',
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    bgcolor: 'transparent',
                  }}
                >
                  {group.label}
                </ListSubheader>
              ) : undefined
            }
          >
            {group.items.map((item, position) => {
              const [light, dark] = COLOUR_CYCLE[(offset + position) % COLOUR_CYCLE.length] ?? [
                BRAND.mint,
                BRAND.ink,
              ];
              const button = (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end
                  onClick={leave}
                  sx={{
                    minHeight: 40,
                    my: 0.25,
                    px: rail ? 0 : 1.5,
                    justifyContent: rail ? 'center' : 'initial',
                    '& .MuiListItemIcon-root': {
                      flexShrink: 0,
                      width: 30,
                      height: 30,
                      mr: rail ? 0 : 1.25,
                      borderRadius: '9px',
                      alignItems: 'center',
                      color: dark,
                      bgcolor: alpha(light, 0.16),
                      // The lucide line icons are sized by width/height, not font size.
                      '& svg': { width: 18, height: 18, strokeWidth: 2 },
                      transition: `color 180ms ${EASE}, background-color 180ms ${EASE}, transform 180ms ${EASE}`,
                    },
                    // The entry under the pointer slides towards the page it opens.
                    '&:hover': {
                      bgcolor: MINT_GLOW,
                      transform: rail ? 'none' : 'translateX(3px)',
                      '& .MuiListItemIcon-root': { transform: 'scale(1.12)' },
                    },
                    '&.Mui-focusVisible': { boxShadow: `0 0 0 3px ${MINT_GLOW_STRONG}` },
                    '&.active, &.active:hover': {
                      color: BRAND.onMint,
                      backgroundImage: accentGradient(),
                      boxShadow: ACCENT_SHADOW,
                      transform: 'none',
                      '& .MuiListItemIcon-root': {
                        color: BRAND.onMint,
                        bgcolor: 'rgba(255, 255, 255, 0.35)',
                      },
                      '& .MuiListItemText-primary': { fontWeight: 600 },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                    {item.icon}
                  </ListItemIcon>
                  {!rail && <ListItemText primary={item.label} />}
                </ListItemButton>
              );
              return (
                <ListItem key={item.to} disablePadding>
                  {rail ? (
                    <Tooltip title={item.label} placement="right">
                      {button}
                    </Tooltip>
                  ) : (
                    button
                  )}
                </ListItem>
              );
            })}
          </List>
        );
      })}
    </Box>
  );
}

/**
 * Back to the previous page within the application. A page opened directly (a bookmark, a link from
 * an email) has no previous page here, so Back goes up instead: a record to its list, any other page
 * to the dashboard.
 */
function BackButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const inApp = location.key !== 'default';
  if (!inApp && location.pathname === '/') return null;
  const go = () => {
    if (inApp) {
      void navigate(-1);
      return;
    }
    const here = locate(
      location.pathname,
      session.data == null ? undefined : visibleNavigation(session.data),
    );
    void navigate(here !== null && here.item.to !== location.pathname ? here.item.to : '/');
  };
  return (
    <Tooltip title="Back">
      <IconButton size="small" aria-label="Back" onClick={go}>
        <ArrowBackIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

/** Where the user is: module › page, from the navigation model. */
function PageContext() {
  const { pathname } = useLocation();
  const session = useSession();
  // The breadcrumb names a page the user can reach: a self-service user's leave request sits under
  // "My leave", not under the HR list they are not offered.
  const here = locate(pathname, session.data == null ? undefined : visibleNavigation(session.data));
  if (here === null) return null;
  return (
    <Breadcrumbs aria-label="Breadcrumb" sx={{ display: { xs: 'none', sm: 'flex' }, minWidth: 0 }}>
      {here.group !== null && (
        <Typography variant="body2" color="text.secondary" noWrap>
          {here.group}
        </Typography>
      )}
      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }} noWrap>
        {here.item.label}
      </Typography>
    </Breadcrumbs>
  );
}

function AccountMenu({ me }: { me: MeResponse }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [dialog, setDialog] = useState<'password' | 'recovery' | null>(null);
  const credential = useOwnCredential();
  const builtIn = credential.data?.builtIn === true;
  // No authenticator in use: no code to confirm with, and no recovery codes to replace.
  const secondFactor = credential.data?.secondFactor === true;
  return (
    <>
      <Button
        color="inherit"
        aria-label="Account"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={(event) => {
          event.currentTarget.focus();
          setAnchor(event.currentTarget);
        }}
        sx={{ gap: 1, minWidth: 0, px: 1 }}
      >
        <PersonAvatar name={me.displayName} userId={me.userId} size={32} />
        <Typography
          variant="body2"
          noWrap
          sx={{ display: { xs: 'none', sm: 'block' }, maxWidth: 220, fontWeight: 600 }}
        >
          {me.displayName}
        </Typography>
      </Button>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => {
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 220 }}>
          <Typography variant="subtitle2">{me.displayName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {me.email}
          </Typography>
        </Box>
        <Divider />
        {builtIn && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setDialog('password');
            }}
          >
            <KeyIcon fontSize="small" sx={{ mr: 1.5 }} />
            Change password
          </MenuItem>
        )}
        {builtIn && secondFactor && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setDialog('recovery');
            }}
          >
            <PasswordIcon fontSize="small" sx={{ mr: 1.5 }} />
            New recovery codes
          </MenuItem>
        )}
        {builtIn && <Divider />}
        <MenuItem
          onClick={() => {
            void signOut(me.csrfToken);
          }}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
          Sign out
        </MenuItem>
      </Menu>
      {dialog === 'password' && (
        <ChangePasswordDialog
          csrfToken={me.csrfToken}
          secondFactor={secondFactor}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'recovery' && (
        <RecoveryCodesDialog
          csrfToken={me.csrfToken}
          remaining={credential.data?.recoveryCodesRemaining ?? 0}
          onClose={() => {
            setDialog(null);
            void credential.refetch();
          }}
        />
      )}
    </>
  );
}
